import React, { useMemo } from "react";
import { Typography, Chip, Box, Divider } from "@mui/material";
import {
    Container,
    HeaderRow,
    LeftHeader,
    RightHeader,
    Section,
    StatusChip,
    DangerChip,
} from "./PaymentConfirmation.styles";

const PAYMENT_STATUS_COLORS = {
    "Awaiting Payment": { border: "#ffeb3b", text: "#f57f17" },
    "Paid": { border: "#4caf50", text: "#1b5e20" },
    "Paid by Credit": { border: "#4caf50", text: "#1b5e20" },
    "Failed": { border: "#f44336", text: "#b71c1c" },
};

const fmt = (v) => `$${Number(v || 0).toFixed(2)}`;

export default function PaymentConfirmation({ order, onOrderChanged }) {
    const orderStatus = order?.status || '';
    const orderStatusUpper = orderStatus.toUpperCase();
    const creditAmount = Number(order?.credit_amount || 0);
    const totalAmount = Number(order?.total_amount || 0);
    const hasCredit = creditAmount > 0;
    const remaining = Math.max(0, Math.round((totalAmount - creditAmount) * 100) / 100);

    const paymentStatus = useMemo(() => {
        const paidStatuses = ['PAID', 'VENDOR ORDER PROCESSED', 'ASSEMBLING ORDER', 'SHIPPED', 'DELIVERED'];
        if (paidStatuses.includes(orderStatusUpper)) {
            if (hasCredit && remaining <= 0) return "Paid by Credit";
            return "Paid";
        }
        return "Awaiting Payment";
    }, [orderStatusUpper, hasCredit, remaining]);

    const statusColor = PAYMENT_STATUS_COLORS[paymentStatus] || PAYMENT_STATUS_COLORS["Awaiting Payment"];

    return (
        <Container>
            <Section>
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                Payment Status
                            </Typography>
                            <Chip
                                label={paymentStatus}
                                sx={{
                                    backgroundColor: '#ffffff',
                                    color: statusColor.text,
                                    borderColor: statusColor.border,
                                    borderWidth: 2,
                                    borderStyle: 'solid',
                                    fontWeight: 700,
                                    fontSize: '0.875rem',
                                    padding: '6px 12px',
                                }}
                            />
                        </Box>
                    </Box>

                    {hasCredit && (
                        <Box sx={{ mt: 2, p: 2, borderRadius: 1, border: '1px solid #e0e0e0', background: '#fafafa' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Credit Details</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">Order Total</Typography>
                                <Typography variant="body2">{fmt(totalAmount)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#2e7d32' }}>Credit Applied</Typography>
                                <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>-{fmt(creditAmount)}</Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" fontWeight={700}>
                                    {remaining <= 0 ? 'Balance' : 'Remaining Balance'}
                                </Typography>
                                <Typography variant="body2" fontWeight={700}>{fmt(remaining)}</Typography>
                            </Box>
                            {remaining <= 0 && (
                                <Typography variant="caption" sx={{ color: '#2e7d32', display: 'block', mt: 0.5 }}>
                                    Fully covered by credit
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </Section>
        </Container>
    );
}
